'use client';

interface OverviewData {
  totalServices: number;
  textsSent: number;
  complianceRate: number | null;
  flagged: number;        // genuine misses today (visit-level)
  needsPhone?: number;    // stops with no customer number — not a tech miss
  repliesWaiting: number;
  avgPhotos: number;
  previousDay: {
    complianceRate: number | null;
    textsSent: number;
    flagged: number;
    repliesWaiting: number;
  } | null;
}

interface ScoreCardsProps {
  data: OverviewData | null;
  loading: boolean;
}

function CompareArrow({ current, previous }: { current: number | null; previous: number | null | undefined }) {
  if (previous === undefined || previous === null || current === null) return null;
  const diff = current - previous;
  if (Math.abs(diff) < 0.5) return <span className="text-gray-400 text-xs ml-1">—</span>;
  const up = diff > 0;
  return (
    <span className={`text-xs ml-1 ${up ? 'text-green-600' : 'text-red-500'}`}>
      {up ? '↑' : '↓'} {Math.abs(Math.round(diff))}{typeof current === 'number' && current <= 100 ? '%' : ''}
    </span>
  );
}

function SkeletonCard() {
  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5 animate-pulse">
      <div className="h-4 bg-gray-200 rounded w-24 mb-3" />
      <div className="h-8 bg-gray-200 rounded w-16 mb-2" />
      <div className="h-3 bg-gray-200 rounded w-20" />
    </div>
  );
}

export default function ScoreCards({ data, loading }: ScoreCardsProps) {
  if (loading || !data) {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {[1, 2, 3, 4].map(i => <SkeletonCard key={i} />)}
      </div>
    );
  }

  // A null rate means nothing countable yet — no red 0% before the first stop.
  const cr = data.complianceRate;
  const rateColor = cr == null ? 'text-gray-400' : cr >= 90 ? 'text-green-600' : cr >= 70 ? 'text-orange-500' : 'text-red-600';
  const rateBg = cr == null ? 'bg-gray-50 border-gray-200' : cr >= 90 ? 'bg-green-50 border-green-200' : cr >= 70 ? 'bg-orange-50 border-orange-200' : 'bg-red-50 border-red-200';

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
      {/* Compliance Rate */}
      <div className={`rounded-xl shadow-sm border p-5 ${rateBg}`}>
        <div className="text-sm font-medium text-gray-500 mb-1">Compliance Rate</div>
        <div className={`text-3xl font-bold ${rateColor} flex items-center`}>
          {cr == null ? '—' : `${Math.round(cr)}%`}
          {data.previousDay && <CompareArrow current={cr} previous={data.previousDay.complianceRate} />}
        </div>
        <div className="text-xs text-gray-500 mt-1">{data.textsSent}/{data.totalServices} services</div>
      </div>

      {/* Texts Sent */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
        <div className="text-sm font-medium text-gray-500 mb-1">Texts Sent</div>
        <div className="text-3xl font-bold text-gray-800 flex items-center">
          {data.textsSent}
          {data.previousDay && <CompareArrow current={data.textsSent} previous={data.previousDay.textsSent} />}
        </div>
        <div className="text-xs text-gray-500 mt-1">of {data.totalServices} services</div>
      </div>

      {/* Flagged */}
      <div className={`rounded-xl shadow-sm border p-5 ${data.flagged > 0 ? 'bg-red-50 border-red-200' : 'bg-white border-gray-100'}`}>
        <div className="text-sm font-medium text-gray-500 mb-1">Missed</div>
        <div className={`text-3xl font-bold ${data.flagged > 0 ? 'text-red-600' : 'text-gray-800'} flex items-center`}>
          {data.flagged}
          {data.previousDay && <CompareArrow current={data.flagged} previous={data.previousDay.flagged} />}
        </div>
        <div className="text-xs text-gray-500 mt-1">
          missed texts
          {typeof data.needsPhone === 'number' && data.needsPhone > 0 && (
            <> · {data.needsPhone} no phone on file</>
          )}
        </div>
      </div>

      {/* Replies Waiting */}
      <div className={`rounded-xl shadow-sm border p-5 ${data.repliesWaiting > 0 ? 'bg-orange-50 border-orange-200' : 'bg-white border-gray-100'}`}>
        <div className="text-sm font-medium text-gray-500 mb-1">Replies Waiting</div>
        <div className={`text-3xl font-bold ${data.repliesWaiting > 0 ? 'text-orange-500' : 'text-gray-800'} flex items-center`}>
          {data.repliesWaiting}
          {data.previousDay && <CompareArrow current={data.repliesWaiting} previous={data.previousDay.repliesWaiting} />}
        </div>
        <div className="text-xs text-gray-500 mt-1">need response</div>
      </div>
    </div>
  );
}

export type { OverviewData };
